import { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Search } from 'lucide-react';
import {
  fetchSubtopicsForTopic,
  type ContentSubtopic,
} from '../../../../utils/contentUtils';

export interface TopicLibraryFilter {
  id: string;
  label: string;
}

export interface TopicLibraryItem {
  id: string;
  title: string;
  description: string;
}

interface SelectedSubtopic {
  id: string;
  title: string;
  durationMinutes: number;
}

interface TopicLibraryProps {
  topics: TopicLibraryItem[];
  filters: TopicLibraryFilter[];
  activeFilter: string;
  searchQuery: string;
  selectedIds: Set<string>;
  /** Subtopic IDs already on the path, keyed by topic ID */
  selectedSubIds?: Record<string, Set<string>>;
  onFilterChange: (filter: string) => void;
  onSearchChange: (query: string) => void;
  onAddTopic: (
    topic: TopicLibraryItem,
    subtopics: SelectedSubtopic[],
  ) => void;
  loading?: boolean;
}

export function TopicLibrary({
  topics,
  filters,
  activeFilter,
  searchQuery,
  selectedIds,
  selectedSubIds,
  onFilterChange,
  onSearchChange,
  onAddTopic,
  loading = false,
}: TopicLibraryProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [loadingSubs, setLoadingSubs] = useState<Set<string>>(new Set());
  const [subtopicsMap, setSubtopicsMap] = useState<
    Record<string, ContentSubtopic[]>
  >({});
  const [checkedSubs, setCheckedSubs] = useState<
    Record<string, Set<string>>
  >({});

  const toggleExpand = useCallback(
    async (topicId: string) => {
      if (expandedId === topicId) {
        setExpandedId(null);
        return;
      }
      setExpandedId(topicId);

      if (!subtopicsMap[topicId] && !loadingSubs.has(topicId)) {
        setLoadingSubs((prev) => new Set(prev).add(topicId));
        try {
          const subs = await fetchSubtopicsForTopic(topicId);
          setSubtopicsMap((prev) => ({ ...prev, [topicId]: subs }));
          // Pre-check subtopics already on the path
          const alreadySelected = selectedSubIds?.[topicId];
          if (alreadySelected && alreadySelected.size > 0) {
            setCheckedSubs((prev) => ({
              ...prev,
              [topicId]: new Set(alreadySelected),
            }));
          }
        } catch (err) {
          console.error('Failed to fetch subtopics', err);
          setSubtopicsMap((prev) => ({ ...prev, [topicId]: [] }));
        } finally {
          setLoadingSubs((prev) => {
            const next = new Set(prev);
            next.delete(topicId);
            return next;
          });
        }
      }
    },
    [expandedId, subtopicsMap, loadingSubs],
  );

  const toggleSub = useCallback((topicId: string, subId: string) => {
    setCheckedSubs((prev) => {
      const current = new Set(prev[topicId] || []);
      if (current.has(subId)) current.delete(subId);
      else current.add(subId);
      return { ...prev, [topicId]: current };
    });
  }, []);

  const handleAddToPath = useCallback(
    (topic: TopicLibraryItem) => {
      const checked = checkedSubs[topic.id] || new Set<string>();
      const subs = subtopicsMap[topic.id] || [];
      const selectedSubs: SelectedSubtopic[] = subs
        .filter((s) => checked.has(s.sub_topic_id))
        .map((s) => ({
          id: s.sub_topic_id,
          title: s.sub_topic_name,
          durationMinutes: 0,
        }));
      onAddTopic(topic, selectedSubs);
      setCheckedSubs((prev) => {
        const next = { ...prev };
        delete next[topic.id];
        return next;
      });
      setExpandedId(null);
    },
    [checkedSubs, subtopicsMap, onAddTopic],
  );

  const availableTopics = topics;

  return (
    <div className="codelive-topic-library">
      <div className="codelive-topic-library__controls">
        <div className="mb-2 position-relative">
          <Search
            size={16}
            className="position-absolute text-muted"
            style={{ left: 10, top: '50%', transform: 'translateY(-50%)' }}
          />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search topics or sub-topics..."
            className="form-control"
            style={{ paddingLeft: 32 }}
          />
        </div>

        {filters.length > 0 && (
          <div className="mb-2 d-flex flex-wrap align-items-center gap-2">
            <span className="text-muted small">Filter by subject</span>
            {filters.map((chip) => (
              <button
                key={chip.id}
                type="button"
                onClick={() => onFilterChange(chip.id)}
                className={`btn btn-sm ${
                  activeFilter === chip.id ? 'btn-primary' : 'btn-outline-primary'
                }`}
              >
                {chip.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="codelive-topic-library__list">
        {loading ? (
          <p className="text-muted small mb-0">Loading topics...</p>
        ) : (
          <div className="d-flex flex-column gap-3 pb-2">
            {availableTopics.map((topic) => {
              const isExpanded = expandedId === topic.id;
              const subs = subtopicsMap[topic.id] || [];
              const checked = checkedSubs[topic.id] || new Set<string>();
              const checkedCount = checked.size;

              return (
                <div
                  key={topic.id}
                  className="card"
                  style={{ borderRadius: 12, overflow: 'hidden' }}
                >
                  <div
                    className="card-body pb-2"
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleExpand(topic.id)}
                    role="button"
                    tabIndex={0}
                    aria-expanded={isExpanded}
                  >
                    <div className="d-flex align-items-start gap-2">
                      <div className="flex-grow-1 min-w-0">
                        <h6 className="mb-0 fw-bold">
                          {topic.title}
                          {selectedIds.has(topic.id) && (
                            <span
                              className="badge ms-2"
                              style={{ background: '#dcfce7', color: '#16a34a', fontSize: 10, fontWeight: 600, verticalAlign: 'middle' }}
                            >
                              On Path
                            </span>
                          )}
                        </h6>
                        <p
                          className="text-muted mb-0"
                          style={{ fontSize: '0.85rem' }}
                        >
                          {topic.description ||
                            'No description available for this topic.'}
                        </p>
                      </div>
                      <div className="d-flex align-items-center gap-2 flex-shrink-0">
                        {checkedCount > 0 && (
                          <span
                            className="badge"
                            style={{
                              background: '#dbeafe',
                              color: '#2563eb',
                              fontSize: 11,
                              fontWeight: 600,
                            }}
                          >
                            {checkedCount} selected
                          </span>
                        )}
                        {isExpanded ? (
                          <ChevronDown size={18} className="text-muted" />
                        ) : (
                          <ChevronRight size={18} className="text-muted" />
                        )}
                      </div>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="px-3 pb-3">
                      {loadingSubs.has(topic.id) ? (
                        <p className="text-muted small mb-0 mt-2">
                          Loading sub-topics...
                        </p>
                      ) : subs.length === 0 ? (
                        <p className="text-muted small mb-0 mt-2">
                          No sub-topics available.
                        </p>
                      ) : (
                        <>
                          <label
                            className="d-flex align-items-center gap-2 rounded px-2 py-1 mt-1 fw-semibold small"
                            style={{ cursor: 'pointer', background: checkedCount === subs.length ? '#eff6ff' : '#f8fafc' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              className="form-check-input m-0"
                              checked={checkedCount === subs.length}
                              onChange={() => {
                                setCheckedSubs((prev) => {
                                  if (checkedCount === subs.length) {
                                    return { ...prev, [topic.id]: new Set<string>() };
                                  }
                                  return { ...prev, [topic.id]: new Set(subs.map((s) => s.sub_topic_id)) };
                                });
                              }}
                            />
                            Select All
                          </label>
                          <div className="d-flex flex-column gap-1 mt-1">
                            {subs.map((sub) => {
                              const isChecked = checked.has(sub.sub_topic_id);
                              const alreadyOnPath = selectedSubIds?.[topic.id]?.has(sub.sub_topic_id) ?? false;
                              return (
                                <label
                                  key={sub.sub_topic_id}
                                  className="d-flex align-items-center gap-2 rounded px-2 py-1"
                                  style={{
                                    cursor: alreadyOnPath ? 'default' : 'pointer',
                                    background: isChecked ? '#eff6ff' : 'transparent',
                                    opacity: alreadyOnPath ? 0.6 : 1,
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <input
                                    type="checkbox"
                                    className="form-check-input m-0"
                                    checked={isChecked}
                                    disabled={alreadyOnPath}
                                    onChange={() =>
                                      toggleSub(topic.id, sub.sub_topic_id)
                                    }
                                  />
                                  <span className="small">
                                    {sub.sub_topic_name}
                                    {alreadyOnPath && <span className="text-muted ms-1">(added)</span>}
                                  </span>
                                </label>
                              );
                            })}
                          </div>

                          <div className="d-flex align-items-center justify-content-between mt-2 pt-2 border-top">
                            <span className="text-muted small">
                              {checkedCount} sub-topic
                              {checkedCount !== 1 ? 's' : ''} selected
                            </span>
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddToPath(topic);
                              }}
                              disabled={checkedCount === 0}
                            >
                              + Add to Path
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {availableTopics.length === 0 && (
              <p className="text-muted small mb-0">
                No topics match your filters. Try another subject or search.
              </p>
            )}
          </div>
        )}
      </div>

      <div className="mt-2">
        <p className="text-muted small mb-0" style={{ fontSize: 11 }}>
          <span style={{ color: '#2563eb' }}>&#9432;</span>{' '}
          Select the sub-topics you want to include. You can set the time on the Learning Path.
        </p>
      </div>
    </div>
  );
}
